import { Component } from "@angular/core";
import { AbstractEffTestingStatusComponent } from "../abstract-eff-testing-status-component";
import { EffectivenessStatusType, SampleScore, TestStepScore } from "../../../shared/enums";
import { EffTableConfig } from "../../common/eff-table/models/eff-table-config.model";
import { EffTableColumnConfig } from "../../common/eff-table/models/eff-table-column-config.model";
import { EvidenceSampleTestStep } from "../models/evidence-sample-step.model";
import { SampleScoreColors, StepScoreColorEnum } from "../models/score-color.enum";
import { SelectColoredOption } from "../../common/eff-colored-selector/models/SelectColoredOption";
import { FinalConclusion } from "../components/eff-final-conclusion-box/models/final-conclusion.model";
import _ from "underscore";
import { NgClass, NgTemplateOutlet } from "@angular/common";
import { EffStepHeaderComponent } from "../../common/eff-step-header/eff-step-header.component";
import { AuthorMessageBoxComponent } from "../components/author-message-box/author-message-box.component";
import { EffCheckboxComponent } from "../../common/eff-checkbox/eff-checkbox.component";
import { EffInlineTextEditorComponent } from "../../common/eff-inline-text-editor/eff-inline-text-editor.component";
import { DateFormatPipe } from "@app/shared/pipes/date-format.pipe";
import { DateTimeFormatPipe } from "@app/shared/pipes/date-time-format.pipe";
import { EffTableComponent, EffTableColumnDirective } from "../../common/eff-table/eff-table.component";
import { EffTextareaComponent } from "../../common/eff-textarea/eff-textarea.component";
import { DocumentsListComponent } from "../components/documents-list/documents-list.component";
import { EffIconComponent } from "../../common/eff-icon/eff-icon.component";
import { EffColoredSelectorComponent } from "../../common/eff-colored-selector/eff-colored-selector.component";
import { EffButtonComponent } from "../../common/eff-button/eff-button.component";
import { EffTestInstructionsInfoBoxComponent } from "../../info-boxes/eff-test-instructions-info-box/eff-test-instructions-info-box.component";
import { EffEvidenceInstructionsInfoBoxComponent } from "../../info-boxes/eff-evidence-instructions-info-box/eff-evidence-instructions-info-box.component";
import { EffSourceDocumentInfoBoxComponent } from "../../info-boxes/eff-source-document-info-box/eff-source-document-info-box.component";
import { EffControlInfoBoxComponent } from "../../info-boxes/eff-control-info-box/eff-control-info-box.component";
import { CerrixSlideOverComponent } from "@app/shared/cerrix-slide-over/cerrix-slide-over.component";
import { TranslatePipe, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/controls/i18n/scope";

@Component({
    selector: "eff-testing-test",
    templateUrl: "./eff-testing-test.component.html",
    styleUrls: ["./eff-testing-test.component.scss"],
    viewProviders: [provideTranslationScope(scope)],
    imports: [
        NgClass,
        NgTemplateOutlet,
        EffStepHeaderComponent,
        AuthorMessageBoxComponent,
        EffCheckboxComponent,
        EffInlineTextEditorComponent,
        DateFormatPipe,
        DateTimeFormatPipe,
        EffTableComponent,
        EffTableColumnDirective,
        EffTextareaComponent,
        DocumentsListComponent,
        EffIconComponent,
        EffColoredSelectorComponent,
        EffButtonComponent,
        EffTestInstructionsInfoBoxComponent,
        EffEvidenceInstructionsInfoBoxComponent,
        EffSourceDocumentInfoBoxComponent,
        EffControlInfoBoxComponent,
        CerrixSlideOverComponent,
        TranslatePipe,
    ]
})
export class EffTestingTestComponent extends AbstractEffTestingStatusComponent {
    protected loadSamples: boolean = true;

    isNonOccurrenceTestPlan = false;

    allEvidenceValid = false;
    sampleScores: SelectColoredOption[] = [];
    stepScores: SelectColoredOption[] = [];

    tableConfig: EffTableConfig = {
        ExpandableRow: true,
        SeparateRows: true,
        DisableRowFunc(item: EvidenceSampleTestStep, items: EvidenceSampleTestStep[]) {
            if (!item.isSample) {
                return false;
            }

            return items.some((item) => !item.isSample && !item.stepScore);
        },
        RowClassFunc(item: EvidenceSampleTestStep) {
            if (item.isSample) {
                return "sample-row";
            }
            return "";
        },
        OnRowClickFunc(item: EvidenceSampleTestStep) {
            if (item.IsExpanded && item.stepScore === 0) {
                setTimeout(() => {
                    item.triggerOpenScore = !item.triggerOpenScore;
                }, 200);
            }
        },
    };

    displayedColumns: Array<EffTableColumnConfig> = [];
    onChangesDebounce = _.debounce(this.onChanges, 400, false);

    onScoreChanged(newScore: number, item: EvidenceSampleTestStep) {
        if (item.isSample) {
            var score = this.sampleScores.find((s) => s.Id == newScore);
            if (score) {
                item.sampleScore = score.Id;
                item.scoreName = score.Label;
            }
        } else {
            var score = this.stepScores.find((s) => s.Id == newScore);
            if (score) {
                item.stepScore = score.Id;
                item.scoreName = score.Label;
            }
        }

        this.onChanges();
    }

    getSampleScoreColor(score: SampleScore) {
        switch (score) {
            case SampleScore.sufficient:
                return SampleScoreColors.Sufficient;
            case SampleScore.inapplicable:
                return SampleScoreColors.Inapplicable;
            case SampleScore.insufficient:
                return SampleScoreColors.Insufficient;
            case SampleScore.noScore:
                return SampleScoreColors.NoScore;
        }
    }

    getStepScoreColor(score: TestStepScore) {
        switch (score) {
            case TestStepScore.correct:
                return StepScoreColorEnum.Correct;
            case TestStepScore.inapplicable:
                return StepScoreColorEnum.Inapplicable;
            case TestStepScore.incorrect:
                return StepScoreColorEnum.Incorrect;
            case TestStepScore.missingEvidence:
                return StepScoreColorEnum.MissingEvidence;
            case TestStepScore.noScore:
                return StepScoreColorEnum.NoScore;
        }
    }

    override onLoad() {
        this.displayedColumns = [
            { PropertyName: "name", DisplayedNameKey: "effGrid.columnName", IsTemplate: true, Width: "40%" },
            {
                PropertyName: "testerComment",
                DisplayedNameKey:
                    this.showReviewColumn && this.pageState.isRejected
                        ? "effGrid.columnScoreTester"
                        : "effGrid.columnCommentTester",
                IsTemplate: true,
                Width: "30%",
            },
        ];

        if (this.showReviewColumn && this.pageState.isRejected) {
            this.displayedColumns.push({
                PropertyName: "commentReviewer",
                DisplayedNameKey: "effGrid.columnCommentReviewer",
                IsTemplate: true,
                Width: "30%",
            });
        } else {
            this.displayedColumns.push({
                PropertyName: "score",
                DisplayedNameKey: "effGrid.columnScore",
                IsTemplate: true,
                Width: "30%",
            });
        }

        this.isNonOccurrenceTestPlan =
            this.effectivenessModel.status == EffectivenessStatusType.nonOccurrence;

        this._ds.getSampleScores().subscribe((scores) => {
            this.sampleScores = scores.map((s) => {
                return {
                    Id: s.value,
                    Label:
                        s.value == 0
                            ? this._translation.t("effTestingTest.addScore")
                            : s.value == SampleScore.inapplicable
                              ? this._translation.t("effTestingTest.notApplicable")
                              : s.label,
                    BackgroundColor: this.getSampleScoreColor(s.value),
                    Color:
                        s.value != 0 && s.value != SampleScore.inapplicable ? "white" : "#001A41",
                    Hide: s.value == 0,
                };
            });
        });

        this._ds.getTestStepScores().subscribe((scores) => {
            this.stepScores = scores.map((s) => {
                return {
                    Id: s.value,
                    Label:
                        s.value == 0
                            ? this._translation.t("effTestingTest.addScore")
                            : s.value == TestStepScore.inapplicable
                              ? this._translation.t("effTestingTest.notApplicable")
                              : s.label,
                    BackgroundColor: this.getStepScoreColor(s.value),
                    Color:
                        s.value != 0 && s.value != TestStepScore.inapplicable ? "white" : "#001A41",
                    Hide: s.value == 0,
                };
            });
        });

        this.onChanges();
    }

    async save(doRefresh: boolean) {
        await this._translation.ready();
        this.updateModels();
        if (this.validateFromSave()) {
            await this.saveChanges(doRefresh);
        }
    }

    async saveChanges(doRefresh: boolean) {
        await this.saveSampleChanges();
        if (doRefresh) {
            this.cerrixTab.refresh();
        }
    }

    onChanges() {
        this.allEvidenceValid = this.validate(false);
    }

    validateFromSave(): boolean {
        let valid = true;
        this.samples.forEach((s) => {
            if (
                // sample score and comment are filled, so all step scores must have a valid score (not noScore)
                s.score != SampleScore.noScore &&
                s.commentTester &&
                s.commentTester.trim().length > 0
            ) {
                s.testSteps.forEach((c) => {
                    if (c.score == TestStepScore.noScore) {
                        this._toastr.warning(
                            this._translation.t("effTestingTest.pleaseEnterScoreOnSampleAndStep", { sampleName: s.name, stepName: c.name }),
                        );
                        valid = false;
                    }
                });
            }
        });

        if (valid) {
            // all steps are ok
            this.samples.forEach((s) => {
                if (
                    (s.commentTester && s.commentTester.trim().length > 0) ||
                    s.score != SampleScore.noScore
                ) {
                    if (!s.commentTester || s.commentTester.trim().length == 0) {
                        this._toastr.warning(this._translation.t("effTestingTest.pleaseEnterCommentOnSample", { sampleName: s.name }));
                        valid = false;
                    }
                    if (s.score == SampleScore.noScore) {
                        this._toastr.warning(this._translation.t("effTestingTest.pleaseEnterScoreOnSample", { sampleName: s.name }));
                        valid = false;
                    }
                }
            });
        }

        return valid;
    }

    validate(showWarning: boolean): boolean {
        this.updateModels();

        let valid = true;
        this.samples.forEach((s) => {
            s.testSteps.forEach((c) => {
                if (c.score == TestStepScore.noScore) {
                    if (showWarning) {
                        this._toastr.warning(
                            this._translation.t("effTestingTest.pleaseEnterScoreOnSampleAndStep", { sampleName: s.name, stepName: c.name }),
                        );
                    }
                    valid = false;
                }
            });
        });
        if (valid) {
            // all steps are ok
            this.samples.forEach((s) => {
                if (!s.commentTester || s.commentTester.trim().length == 0) {
                    if (showWarning) {
                        this._toastr.warning(this._translation.t("effTestingTest.pleaseEnterCommentOnSample", { sampleName: s.name }));
                    }
                    valid = false;
                }
                if (s.score == SampleScore.noScore) {
                    if (showWarning) {
                        this._toastr.warning(this._translation.t("effTestingTest.pleaseEnterScoreOnSample", { sampleName: s.name }));
                    }
                    valid = false;
                }
            });
        }
        return valid;
    }

    isSampleDisabled(item: any): boolean {
        if (item.isSample) {
            return item.model.testSteps.map((x) => x.score > 0).some((x) => x === false);
        }
        return false;
    }

    async onFinalConclusionClick() {
        await this._translation.ready();

        if (!this.stepPermissions.isCompleteAllowed) {
            return;
        }

        if (!this.validate(true)) {
            return;
        }

        await this.saveChanges(false);

        this.giveFinalConclusion(
            this._translation.t("effTestingTest.testerFinalConclusionsTitle"),
            this._translation.t("effTestingTest.saveFinalConclusion"),
            this._translation.t("effTestingTest.finalConclusionMessage"),
            ((finalConclusion: FinalConclusion) => {
                this._workflowDs
                    .postWorkflowAcceptTester(this.effectivenessModel.guid, {
                        comment: finalConclusion.conclusion,
                        scoreId: finalConclusion.score,
                    })
                    .subscribe(() => {
                        this.cerrixTab.refresh();
                    });
            }).bind(this),
        );
    }

    async onMissingEvidenceClick() {
        await this._translation.ready();

        if (!this.stepPermissions.isCompleteAllowed) {
            return;
        }

        if (!this.validate(true)) {
            return;
        }

        await this.saveChanges(false);

        this.giveFinalConclusion(
            this._translation.t("effTestingTest.missingEvidenceTitle"),
            this._translation.t("effTestingTest.missingEvidenceConfirm"),
            this._translation.t("effTestingTest.missingEvidenceMessage"),
            ((finalConclusion: FinalConclusion) => {
                this._workflowDs
                    .postWorkflowRejectTester(this.effectivenessModel.guid, {
                        comment: finalConclusion.conclusion,
                        scoreId: finalConclusion.score,
                    })
                    .subscribe(() => {
                        this.cerrixTab.refresh();
                    });
            }).bind(this),
            "600px",
            true,
        );
    }

    async onRejectNonOccurrenceClick() {
        await this._translation.ready();

        if (!this.stepPermissions.isCompleteAllowed) {
            return;
        }

        if (!this.validate(true)) {
            return;
        }

        this.giveFinalConclusion(
            this._translation.t("effTestingTest.rejectNonOccurrenceTitle"),
            this._translation.t("effTestingTest.rejectNonOccurrenceButtonLabel"),
            this._translation.t("effTestingTest.rejectNonOccurrenceMessage"),
            ((finalConclusion: FinalConclusion) => {
                this._workflowDs
                    .postWorkflowNonOccurrenceRejectTester(this.effectivenessModel.guid, {
                        comment: finalConclusion.conclusion,
                        scoreId: finalConclusion.score,
                    })
                    .subscribe(() => {
                        this.cerrixTab.refresh();
                    });
            }).bind(this),
            "600px",
            true,
        );
    }

    async onAcceptNonOccurrenceClick() {
        await this._translation.ready();

        if (!this.stepPermissions.isCompleteAllowed) {
            return;
        }

        if (!this.validate(true)) {
            return;
        }

        this.giveFinalConclusion(
            this._translation.t("effTestingTest.acceptNonOccurrenceTitle"),
            this._translation.t("effTestingTest.acceptNonOccurrenceButtonLabel"),
            this._translation.t("effTestingTest.acceptNonOccurrenceMessage"),
            ((finalConclusion: FinalConclusion) => {
                this._workflowDs
                    .postWorkflowNonOccurrenceAcceptTester(this.effectivenessModel.guid, {
                        comment: finalConclusion.conclusion,
                        scoreId: finalConclusion.score,
                    })
                    .subscribe(() => {
                        this.cerrixTab.refresh();
                    });
            }).bind(this),
        );
    }
}
